/**
 * Lightweight client error beacon.
 *
 * The reported faults on the old app were all intermittent ("sometimes",
 * "usually after several scans"), which is exactly the class of problem you
 * cannot fix from recollection. This records what actually happened on the
 * device so the next report comes with evidence attached.
 *
 * Deliberately minimal: no third-party SDK (that would reintroduce the
 * runtime-CDN dependency this rebuild exists to remove), no personal data, and
 * it never throws or blocks the UI.
 */

export type ClientEventLevel = 'error' | 'warn' | 'info'

export interface ClientEvent {
  level: ClientEventLevel
  /** Stable short identifier, e.g. 'camera.acquire_failed'. */
  event: string
  // `| undefined` is required by exactOptionalPropertyTypes: these are
  // genuinely "present but unknown", not "absent".
  message?: string | undefined
  stack?: string | undefined
  context?: Record<string, unknown> | undefined
}

interface Envelope extends ClientEvent {
  at: string
  session: string
  ua: string
  display: 'standalone' | 'browser'
  online: boolean
  appVersion: string
}

const ENDPOINT = '/api/telemetry'
const MAX_PER_SESSION = 40

const sessionId = crypto.randomUUID()
let sent = 0

/** Ring buffer of recent events, surfaced in the recovery screen for support. */
const recent: Envelope[] = []
const RECENT_LIMIT = 20

function envelope(e: ClientEvent): Envelope {
  return {
    ...e,
    at: new Date().toISOString(),
    session: sessionId,
    ua: navigator.userAgent,
    display: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser',
    online: navigator.onLine,
    appVersion: __APP_VERSION__,
  }
}

export function report(e: ClientEvent): void {
  let payload: Envelope
  try {
    payload = envelope(e)
  } catch {
    return
  }

  recent.push(payload)
  if (recent.length > RECENT_LIMIT) recent.shift()

  if (import.meta.env.DEV) {
    console[e.level === 'error' ? 'error' : 'warn'](`[${e.event}]`, e.message ?? '', e.context ?? '')
  }

  // Cap per session so a hot error loop can't hammer the network or the Worker.
  if (sent >= MAX_PER_SESSION) return
  sent += 1

  try {
    const body = JSON.stringify(payload)
    // sendBeacon survives the page being backgrounded or torn down, which is
    // precisely when the interesting failures happen on iOS.
    if (navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'application/json' }))) return

    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* telemetry must never surface its own failure */
    })
  } catch {
    /* ignore */
  }
}

export function recentEvents(): readonly Envelope[] {
  return recent
}

/**
 * Catch what React's error boundary structurally cannot: errors thrown outside
 * the render tree, and rejected promises with no handler.
 */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('error', (ev) => {
    // Resource load failures (script/img/link) surface here with no `error`.
    if (ev.error == null && ev.target !== window) {
      const el = ev.target as Element | null
      const src =
        el instanceof HTMLScriptElement || el instanceof HTMLImageElement
          ? el.src
          : el instanceof HTMLLinkElement
            ? el.href
            : 'unknown resource'
      report({ level: 'error', event: 'resource.load_failed', message: src })
      return
    }
    report({
      level: 'error',
      event: 'window.error',
      message: ev.message,
      stack: ev.error instanceof Error ? ev.error.stack : undefined,
    })
  }, true)

  window.addEventListener('unhandledrejection', (ev) => {
    const r: unknown = ev.reason
    report({
      level: 'error',
      event: 'promise.unhandled_rejection',
      message: r instanceof Error ? r.message : String(r),
      stack: r instanceof Error ? r.stack : undefined,
    })
  })
}
